/**
 * useKeyboardNavigation - Keyboard Navigation Hook for THE ONESCENT DIAL
 * 
 * Provides comprehensive keyboard navigation for the dial and application.
 * 
 * KEYBOARD SHORTCUTS:
 * - Arrow Left/Right: Cycle through zodiac signs
 * - Arrow Up/Down: Cycle through seasons
 * - 1-8: Jump to sabbats (Yule=1, Imbolc=2, Ostara=3, Beltane=4, Litha=5, Lammas=6, Mabon=7, Samhain=8)
 * - Enter/Space: Open info panel for focused element
 * - Escape: Close any open panel/overlay
 * - ?: Show keyboard shortcuts help
 * - T: Cycle themes (dark → light → high-contrast → dark)
 * - G: Open glossary
 * - P: Open profile
 */

import { useCallback, useEffect, useState } from 'react';

// Zodiac signs in order
const ZODIAC_SIGNS = [
  'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
  'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
];

// Seasons in order
const SEASONS = ['Spring', 'Summer', 'Autumn', 'Winter'];

// Sabbats in order (matching number keys 1-8)
const SABBATS = [
  'Yule',      // 1 - Winter Solstice
  'Imbolc',    // 2 - Early Spring
  'Ostara',    // 3 - Spring Equinox
  'Beltane',   // 4 - Early Summer
  'Litha',     // 5 - Summer Solstice
  'Lammas',    // 6 - Early Autumn
  'Mabon',     // 7 - Autumn Equinox
  'Samhain'    // 8 - Early Winter
];

// Global shortcuts configuration
export const KEYBOARD_SHORTCUTS = {
  help: { key: '?', description: 'Show keyboard shortcuts' },
  theme: { key: 't', description: 'Cycle themes (Dark → Light → High Contrast)' },
  glossary: { key: 'g', description: 'Open glossary' },
  profile: { key: 'p', description: 'Open profile' },
  escape: { key: 'Escape', description: 'Close panels/overlays' },
  enter: { key: 'Enter', description: 'Open info panel for focused element' },
  space: { key: ' ', description: 'Open info panel for focused element' },
  arrowLeft: { key: 'ArrowLeft', description: 'Previous zodiac sign' },
  arrowRight: { key: 'ArrowRight', description: 'Next zodiac sign' },
  arrowUp: { key: 'ArrowUp', description: 'Previous season' },
  arrowDown: { key: 'ArrowDown', description: 'Next season' },
  numbers: { key: '1-8', description: 'Jump to sabbat (1=Yule, 2=Imbolc, etc.)' }
};

/**
 * Main keyboard navigation hook
 */
export const useKeyboardNavigation = ({
  onElementSelect,
  onThemeChange,
  onGlossaryToggle,
  onProfileToggle,
  onHelpToggle,
  onEscape,
  enabled = true,
  currentZodiac = null,
  currentSeason = null
}) => {
  // Track focused element type and index
  const [focusedElement, setFocusedElement] = useState({
    type: 'zodiac', // 'zodiac', 'season', 'sabbat'
    index: 0
  });
  
  // Track if keyboard navigation is active (user has used keyboard)
  const [isKeyboardActive, setIsKeyboardActive] = useState(false);

  // Get current zodiac index based on actual data
  const getCurrentZodiacIndex = useCallback(() => {
    if (currentZodiac) {
      const idx = ZODIAC_SIGNS.findIndex(z => z.toLowerCase() === currentZodiac.toLowerCase());
      return idx >= 0 ? idx : 0;
    }
    return focusedElement.type === 'zodiac' ? focusedElement.index : 0;
  }, [currentZodiac, focusedElement]);

  // Get current season index
  const getCurrentSeasonIndex = useCallback(() => {
    if (currentSeason) {
      const idx = SEASONS.findIndex(s => s.toLowerCase() === currentSeason.toLowerCase());
      return idx >= 0 ? idx : 0;
    }
    return focusedElement.type === 'season' ? focusedElement.index : 0;
  }, [currentSeason, focusedElement]);

  // Navigate zodiac
  const navigateZodiac = useCallback((direction) => {
    const currentIdx = getCurrentZodiacIndex();
    const newIdx = direction === 'next' 
      ? (currentIdx + 1) % ZODIAC_SIGNS.length
      : (currentIdx - 1 + ZODIAC_SIGNS.length) % ZODIAC_SIGNS.length;
    
    setFocusedElement({ type: 'zodiac', index: newIdx });
    
    if (onElementSelect) {
      onElementSelect('zodiac', ZODIAC_SIGNS[newIdx]);
    }
  }, [getCurrentZodiacIndex, onElementSelect]);

  // Navigate seasons
  const navigateSeason = useCallback((direction) => {
    const currentIdx = getCurrentSeasonIndex();
    const newIdx = direction === 'next'
      ? (currentIdx + 1) % SEASONS.length
      : (currentIdx - 1 + SEASONS.length) % SEASONS.length;
    
    setFocusedElement({ type: 'season', index: newIdx });
    
    if (onElementSelect) {
      onElementSelect('season', SEASONS[newIdx]);
    }
  }, [getCurrentSeasonIndex, onElementSelect]);

  // Jump to sabbat by number
  const jumpToSabbat = useCallback((number) => {
    const idx = number - 1; // Convert 1-8 to 0-7
    if (idx >= 0 && idx < SABBATS.length) {
      setFocusedElement({ type: 'sabbat', index: idx });
      
      if (onElementSelect) {
        onElementSelect('sabbat', SABBATS[idx]);
      }
    }
  }, [onElementSelect]);

  // Open info panel for current focused element
  const openFocusedElement = useCallback(() => {
    const { type, index } = focusedElement;
    let elementKey;
    
    switch (type) {
      case 'zodiac':
        elementKey = ZODIAC_SIGNS[index];
        break;
      case 'season':
        elementKey = SEASONS[index];
        break;
      case 'sabbat':
        elementKey = SABBATS[index];
        break;
      default:
        return;
    }
    
    if (onElementSelect) {
      onElementSelect(type, elementKey);
    }
  }, [focusedElement, onElementSelect]);

  // Main keyboard handler
  const handleKeyDown = useCallback((event) => {
    if (!enabled) return;
    
    // Ignore if typing in input/textarea
    const target = event.target;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
      // Only allow Escape in inputs
      if (event.key !== 'Escape') return;
    }
    
    // Mark keyboard as active
    setIsKeyboardActive(true);
    
    const key = event.key;
    
    // Handle number keys 1-8 for sabbats
    if (/^[1-8]$/.test(key)) {
      event.preventDefault();
      jumpToSabbat(parseInt(key, 10));
      return;
    }
    
    switch (key) {
      // Arrow navigation
      case 'ArrowLeft':
        event.preventDefault();
        navigateZodiac('prev');
        break;
        
      case 'ArrowRight':
        event.preventDefault();
        navigateZodiac('next');
        break;
        
      case 'ArrowUp':
        event.preventDefault();
        navigateSeason('prev');
        break;
        
      case 'ArrowDown':
        event.preventDefault();
        navigateSeason('next');
        break;
      
      // Open focused element
      case 'Enter':
      case ' ':
        event.preventDefault();
        openFocusedElement();
        break;
      
      // Close/escape
      case 'Escape':
        event.preventDefault();
        if (onEscape) onEscape();
        break;
      
      // Help
      case '?':
        event.preventDefault();
        if (onHelpToggle) onHelpToggle();
        break;
      
      // Theme toggle
      case 't':
      case 'T':
        if (!event.ctrlKey && !event.metaKey) {
          event.preventDefault();
          if (onThemeChange) onThemeChange();
        }
        break;
      
      // Glossary
      case 'g':
      case 'G':
        if (!event.ctrlKey && !event.metaKey) {
          event.preventDefault();
          if (onGlossaryToggle) onGlossaryToggle();
        }
        break;
      
      // Profile
      case 'p':
      case 'P':
        if (!event.ctrlKey && !event.metaKey) {
          event.preventDefault();
          if (onProfileToggle) onProfileToggle();
        }
        break;
      
      default:
        break;
    }
  }, [
    enabled,
    jumpToSabbat,
    navigateZodiac,
    navigateSeason,
    openFocusedElement,
    onEscape,
    onHelpToggle,
    onThemeChange,
    onGlossaryToggle,
    onProfileToggle
  ]);

  // Track mouse usage to hide keyboard focus indicators
  const handleMouseDown = useCallback(() => {
    setIsKeyboardActive(false);
  }, []);

  // Attach/detach event listeners
  useEffect(() => {
    if (enabled) {
      window.addEventListener('keydown', handleKeyDown);
      window.addEventListener('mousedown', handleMouseDown);
      
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
        window.removeEventListener('mousedown', handleMouseDown);
      };
    }
  }, [enabled, handleKeyDown, handleMouseDown]);

  return {
    focusedElement,
    setFocusedElement,
    isKeyboardActive,
    navigateZodiac,
    navigateSeason,
    jumpToSabbat,
    openFocusedElement,
    // Export constants for reference
    ZODIAC_SIGNS,
    SEASONS,
    SABBATS,
    KEYBOARD_SHORTCUTS
  };
};

/**
 * useReducedMotion - Detects user's motion preference
 */
export const useReducedMotion = () => {
  // Initialize with actual value to avoid hydration mismatch
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });
  
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    // Only subscribe to changes, initial value set via useState initializer
    const handler = (e) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handler);
    
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);
  
  return prefersReducedMotion;
};

/**
 * useFocusVisible - Tracks focus-visible state for accessibility
 */
export const useFocusVisible = () => {
  const [isFocusVisible, setIsFocusVisible] = useState(false);
  
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Tab') {
        setIsFocusVisible(true);
      }
    };
    
    const handleMouseDown = () => {
      setIsFocusVisible(false);
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('mousedown', handleMouseDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('mousedown', handleMouseDown);
    };
  }, []);
  
  return isFocusVisible;
};

export default useKeyboardNavigation;
